import React, { useState, useContext } from 'react';
import { 
  Container, Typography, Box, Button, Dialog, 
  DialogContent, DialogActions, Table, TableBody, TableCell, 
  TableHead, TableRow, IconButton, Chip, CircularProgress, Alert, Tabs, Tab,
  TextField, InputAdornment
} from '@mui/material';
import { StyledTableContainer, tableRowHoverStyles, iconButtonStyles, StyledDialogTitle } from '../components/StyledComponents';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import { useQuery, useMutation, gql } from '@apollo/client';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import VisitForm from '../components/VisitForm';
import AuthContext from '../contexts/AuthContext';
import VisitDetails from '../components/VisitDetails';

// GraphQL queries et mutations
const DELETE_VISIT = gql`
  mutation DeleteVisit($id: ID!) {
    deleteVisit(id: $id) {
      success
      message
    }
  }
`;

// Requête pour obtenir toutes les visites
const GET_ALL_VISITS = gql`
  query GetAllVisits($departmentId: ID, $hostId: ID, $visitorId: ID) {
    visits(departmentId: $departmentId, hostId: $hostId, visitorId: $visitorId) {
      id
      visitor {
        id
        firstName
        lastName
        email
      }
      host {
        id
        firstName
        lastName
        email
      }
      department {
        id
        name
      }
      purpose
      scheduledAt
      status
      rejectionReason
      createdAt
      updatedAt
      documents {
        id
        fileName
        documentUrl
      }
      appointment {
        id
        documents {
          id
          fileName
          documentUrl
        }
      }
    }
  }
`;

function Visits() {
  const { user } = useContext(AuthContext);
  const canViewDocuments = user?.role !== 'ACCEUIL';
  
  // État pour les onglets
  const [tabValue, setTabValue] = useState(0);
  
  // État pour la recherche
  const [searchQuery, setSearchQuery] = useState('');
  
  // États pour les dialogues
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  
  // État pour la visite sélectionnée
  const [selectedVisit, setSelectedVisit] = useState(null);
  
  // État pour les messages d'erreur
  const [errorMessage, setErrorMessage] = useState('');
  
  // Déterminer les variables de la requête selon l'onglet et le rôle
  const getQueryVariables = () => {
    if (user.role === 'VISITOR') {
      return { visitorId: user.id };
    } else if (user.role === 'HOST') {
      if (tabValue === 0) {
        return { hostId: user.id };
      } else {
        return { departmentId: user.department?.id };
      }
    } else if (user.role === 'EMPLOYEE') {
      return { hostId: user.id };
    } else if (user.role === 'MANAGER' || user.role === 'ACCEUIL') {
      return { departmentId: user.department?.id };
    } else {
      // ADMIN
      return {};
    }
  };
  
  // Récupérer les visites selon l'onglet et le rôle
  const { data: visitsData, refetch: refetchVisits } = useQuery(GET_ALL_VISITS, {
    variables: getQueryVariables(),
    fetchPolicy: 'network-only'
  });
  
  // Mutation pour supprimer une visite
  const [deleteVisit, { loading: deleteLoading }] = useMutation(DELETE_VISIT, {
    onCompleted: (data) => {
      if (data.deleteVisit.success) {
        setOpenDeleteDialog(false);
        setSelectedVisit(null);
        setErrorMessage('');
        refetchVisits();
      } else {
        setErrorMessage(data.deleteVisit.message || "Une erreur est survenue lors de la suppression");
      }
    },
    onError: (error) => {
      console.error("Erreur lors de la suppression:", error);
      setErrorMessage(`Erreur: ${error.message}`);
    }
  });
  
  // Gérer le changement d'onglet
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  // Gérer l'ouverture du dialogue de création
  const handleOpenCreateDialog = () => {
    setOpenCreateDialog(true);
  };
  
  // Gérer la fermeture du dialogue de création
  const handleCloseCreateDialog = () => {
    setOpenCreateDialog(false);
  };
  
  // Gérer l'ouverture du dialogue de modification
  const handleOpenEditDialog = (visit) => {
    setSelectedVisit(visit);
    setOpenEditDialog(true);
  };
  
  // Gérer la fermeture du dialogue de modification
  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setSelectedVisit(null);
  };
  
  // Gérer l'ouverture des détails d'une visite
  const handleOpenDetails = (visit) => {
    setSelectedVisit(visit);
    setOpenDetailsDialog(true);
  };
  
  // Gérer la fermeture des détails
  const handleCloseDetails = () => {
    setOpenDetailsDialog(false);
    setSelectedVisit(null);
  };
  
  // Gérer l'ouverture du dialogue de suppression
  const handleOpenDeleteDialog = (visit) => {
    setSelectedVisit(visit);
    setOpenDeleteDialog(true);
  };
  
  // Gérer la fermeture du dialogue de suppression
  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setSelectedVisit(null);
    setErrorMessage('');
  };
  
  // Gérer la suppression d'une visite
  const handleDeleteVisit = () => {
    if (selectedVisit) {
      deleteVisit({
        variables: {
          id: selectedVisit.id
        }
      });
    }
  };
  
  // Gérer le succès de la création d'une visite
  const handleCreateSuccess = (visit) => {
    setOpenCreateDialog(false);
    refetchVisits();
  };
  
  // Gérer le succès de la modification d'une visite
  const handleEditSuccess = (visit) => {
    setOpenEditDialog(false);
    setSelectedVisit(null);
    refetchVisits();
  };
  
  // Gérer la mise à jour d'une visite
  const handleVisitUpdate = (updatedVisit) => {
    refetchVisits();
    handleCloseDetails();
  };
  
  // Vérifier si l'utilisateur peut modifier une visite
  const canEditVisit = (visit) => {
    if (visit.status === 'COMPLETED') {
      return false;
    }
    if (user.role === 'ADMIN' || user.role === 'MANAGER' || user.role === 'ACCEUIL') {
      return true;
    }
    
    if ((user.role === 'HOST' || user.role === 'EMPLOYEE') && visit.host.id === user.id) {
      return true;
    }
    
    if (user.role === 'VISITOR' && visit.visitor?.id === user.id && visit.status === 'PENDING') {
      return true;
    }
    
    return false;
  };
  
  // Vérifier si l'utilisateur peut supprimer une visite
  const canDeleteVisit = (visit) => {
    if (visit.status === 'COMPLETED') {
      return false;
    }
    if (['ADMIN', 'MANAGER', 'ACCEUIL'].includes(user?.role)) {
      return true;
    }
    
    if (user.role === 'VISITOR' && visit.visitor?.id === user.id && ['PENDING', 'CANCELLED'].includes(visit.status)) {
      return true;
    }
    
    return false;
  };
  
  // Obtenir le libellé du statut
  const getStatusLabel = (status) => {
    switch (status) {
      case 'PENDING':
        return 'En attente';
      case 'APPROVED':
        return 'Approuvée';
      case 'REJECTED':
        return 'Rejetée';
      case 'COMPLETED':
        return 'Terminée';
      case 'CANCELLED':
        return 'Annulée';
      default:
        return status;
    }
  };
  
  // Obtenir la couleur du statut
  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING':
        return 'warning';
      case 'APPROVED':
        return 'success';
      case 'REJECTED':
        return 'error';
      case 'COMPLETED':
        return 'info';
      case 'CANCELLED':
        return 'default';
      default:
        return 'default';
    }
  };
  
  // Filtrer les visites en fonction de la recherche
  const filteredVisits = visitsData?.visits?.filter(visit => {
    const searchLower = searchQuery.toLowerCase();
    const visitorFirstName = visit.visitor?.firstName || '';
    const visitorLastName = visit.visitor?.lastName || '';
    return (
      visitorFirstName.toLowerCase().includes(searchLower) ||
      visitorLastName.toLowerCase().includes(searchLower) ||
      visit.host.firstName.toLowerCase().includes(searchLower) ||
      visit.host.lastName.toLowerCase().includes(searchLower) ||
      visit.department.name.toLowerCase().includes(searchLower) ||
      visit.purpose.toLowerCase().includes(searchLower)
    );
  }) || [];

  const renderVisitDocuments = (visit) => {
    const docs = visit?.appointment?.documents || [];
    if (!docs.length) {
      return <Typography variant="body2" color="text.secondary">-</Typography>;
    }
    return (
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
        {docs.map((doc) => (
          <IconButton
            key={doc.id}
            size="small"
            component="a"
            href={doc.documentUrl}
            target="_blank"
            rel="noreferrer"
            title={`Télécharger ${doc.fileName}`}
            sx={iconButtonStyles}
          >
            <DownloadIcon fontSize="small" />
          </IconButton>
        ))}
      </Box>
    );
  };
  
  return (
    <Container maxWidth="lg" sx={{ py: 2 }}>
      <Typography variant="h4" gutterBottom>
        Gestion Electronique des Visites
      </Typography>
      
      {user.role === 'HOST' && (
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          sx={{ mb: 2 }}
        >
          <Tab label="Mes visites" />
          <Tab label="Visites du département" />
        </Tabs>
      )}
      
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">
          {user.role === 'VISITOR' ? 'Mes visites' : 
           user.role === 'HOST' && tabValue === 0 ? 'Mes visites' : 
           user.role === 'HOST' && tabValue === 1 ? 'Visites du département' : 
           user.role === 'EMPLOYEE' ? 'Mes visites' :
           (user.role === 'MANAGER' || user.role === 'ACCEUIL') ? 'Visites du département' : 
           'Toutes les visites'}
        </Typography>
        
        {(user.role === 'VISITOR' || user.role === 'ADMIN' || user.role === 'MANAGER' || user.role === 'ACCEUIL') && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenCreateDialog}
          >
            Nouvelle Visite
          </Button>
        )}
      </Box>
      
      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Rechercher une visite..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      
      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}
      
      <StyledTableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Visiteur</TableCell>
              <TableCell>Hôte</TableCell>
              <TableCell>Département</TableCell>
              <TableCell>Date et heure</TableCell>
              <TableCell>Statut</TableCell>
              {canViewDocuments && <TableCell>Pièces jointes</TableCell>}
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredVisits.map((visit, index) => (
              <TableRow 
                key={visit.id}
                sx={{
                  backgroundColor: index % 2 === 0 ? 'transparent' : 'rgba(0, 0, 0, 0.04)',
                  ...tableRowHoverStyles
                }}
              >
                <TableCell>
                  {`${visit.visitor?.firstName || ''} ${visit.visitor?.lastName || ''}`.trim() || visit.visitor?.email || 'Visiteur'}
                </TableCell>
                <TableCell>{visit.host.firstName} {visit.host.lastName}</TableCell>
                <TableCell>{visit.department.name}</TableCell>
                <TableCell>
                  {format(new Date(visit.scheduledAt), 'PPP à HH:mm', { locale: fr })}
                </TableCell>
                <TableCell>
                  <Chip 
                    label={getStatusLabel(visit.status)} 
                    color={getStatusColor(visit.status)} 
                    size="small" 
                  />
                </TableCell>
                {canViewDocuments && <TableCell>{renderVisitDocuments(visit)}</TableCell>}
                <TableCell sx={{ whiteSpace: 'nowrap', '& > *': { display: 'inline-block', marginRight: '4px' } }}>
                  <IconButton
                    color="info"
                    onClick={() => handleOpenDetails(visit)}
                    sx={iconButtonStyles}
                    size="small"
                  >
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                  
                  {canEditVisit(visit) && (
                    user.role === 'HOST' ? (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleOpenEditDialog(visit)}
                        sx={{ minWidth: 'auto', textTransform: 'none', mr: 0.5 }}
                      >
                        Transferer
                      </Button>
                    ) : (
                      <IconButton
                        color="primary"
                        onClick={() => handleOpenEditDialog(visit)}
                        sx={iconButtonStyles}
                        size="small"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    )
                  )}
                  
                  {canDeleteVisit(visit) && (
                    <IconButton
                      color="error"
                      onClick={() => handleOpenDeleteDialog(visit)}
                      sx={iconButtonStyles}
                      size="small"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
            
            {filteredVisits.length === 0 && (
              <TableRow>
                <TableCell colSpan={canViewDocuments ? 7 : 6} align="center">
                  {searchQuery ? 'Aucune visite ne correspond à votre recherche' : 'Aucune visite trouvée'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </StyledTableContainer>
      
      {/* Dialogue de création de visite */}
      <Dialog 
        open={openCreateDialog} 
        onClose={handleCloseCreateDialog}
        maxWidth="md"
        fullWidth
      >
        <StyledDialogTitle>Créer une nouvelle visite</StyledDialogTitle>
        <DialogContent>
          <VisitForm onSuccess={handleCreateSuccess} />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateDialog}>Annuler</Button>
        </DialogActions>
      </Dialog>
      
      {/* Dialogue de modification de visite */}
      <Dialog 
        open={openEditDialog} 
        onClose={handleCloseEditDialog}
        maxWidth="md"
        fullWidth
      >
        <StyledDialogTitle>Modifier la visite</StyledDialogTitle>
        <DialogContent>
          <VisitForm 
            onSuccess={handleEditSuccess} 
            initialData={selectedVisit}
            isEdit={true}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>Annuler</Button>
        </DialogActions>
      </Dialog>
      
      {/* Dialogue des détails de la visite */}
      {selectedVisit && (
        <VisitDetails
          visit={selectedVisit}
          open={openDetailsDialog}
          onClose={handleCloseDetails}
          onUpdate={handleVisitUpdate}
        />
      )}
      
      {/* Dialogue de suppression de visite */}
      <Dialog 
        open={openDeleteDialog} 
        onClose={handleCloseDeleteDialog}
      >
        <StyledDialogTitle>Confirmer la suppression</StyledDialogTitle>
        <DialogContent>
          {selectedVisit && (
            <Typography>
              Êtes-vous sûr de vouloir supprimer cette visite prévue le {format(new Date(selectedVisit.scheduledAt), 'PPP', { locale: fr })} ?
            </Typography>
          )}
          {errorMessage && (
            <Typography color="error" sx={{ mt: 2 }}>
              {errorMessage}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Annuler</Button>
          <Button 
            onClick={handleDeleteVisit} 
            color="error"
            disabled={deleteLoading || !selectedVisit}
          >
            {deleteLoading ? <CircularProgress size={24} /> : 'Supprimer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default Visits; 
